import { useState, useEffect, useCallback } from "react";
import { usePoll } from "@/hooks/usePoll";
import {
  getClassifyQueue,
  recheckUnidentified as recheckUnidentifiedApi,
  type ClassifyItem,
  type WaitingDoc,
  type BalanceParkedBatch,
} from "../api/client";

const POLL_INTERVAL_MS = 30_000;

export interface UseClassifyQueueResult {
  items: ClassifyItem[];
  /** R-262 5d-5b — held batch-mode docs keyed by batchId. */
  waiting: Record<string, WaitingDoc[]>;
  /** R-264 5e-2 — batches parked in balance_review. */
  balanceParked: BalanceParkedBatch[];
  /** R-268 6f — parked-unidentified docs eligible for re-score, keyed by batchId. */
  reresolvable: Record<string, number>;
  /** CS-2 §7 — whether the caller may purge pending-deletion docs. */
  canDeleteInClassify: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  /** R-268 6f — POST recheck-unidentified for a batch then refetch. */
  recheckUnidentified: (batchId: string) => Promise<{ rechecked: number }>;
}

export function useClassifyQueue(jobTypeId?: string): UseClassifyQueueResult {
  const [items, setItems] = useState<ClassifyItem[]>([]);
  const [waiting, setWaiting] = useState<Record<string, WaitingDoc[]>>({});
  const [balanceParked, setBalanceParked] = useState<BalanceParkedBatch[]>([]);
  const [reresolvable, setReresolvable] = useState<Record<string, number>>({});
  const [canDeleteInClassify, setCanDeleteInClassify] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await getClassifyQueue(jobTypeId);
      setItems(data.items);
      setWaiting(data.waiting);
      setBalanceParked(data.balanceParked ?? []);
      setReresolvable(data.reresolvable ?? {});
      setCanDeleteInClassify(data.canDeleteInClassify ?? false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load classification queue");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  usePoll(refresh, POLL_INTERVAL_MS);

  const recheckUnidentified = useCallback(
    async (batchId: string): Promise<{ rechecked: number }> => {
      const result = await recheckUnidentifiedApi(batchId);
      await refresh();
      return result;
    },
    [refresh],
  );

  return { items, waiting, balanceParked, reresolvable, canDeleteInClassify, isLoading, error, refresh, recheckUnidentified };
}
